import type { FC } from 'react';
import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { T, useT } from '@transifex/react';

import InfoWindow from 'views/components/InfoWindow';
import { useWidget } from 'utilities';
import type { TextChartProps } from './types';
import DownloadCsv from '../DownloadCsv';

export const TextChart: FC<TextChartProps> = ({
  slug,
  name,
  analysisQuery,
  analysisBody,
  analysisTextTemplate,
  shortMeta,
  info,
  geojson,
}: TextChartProps) => {
  const t = useT();

  const { rootWidgetProps, loaded, data, noData } = useWidget(
    { slug, geojson },
    { type: 'text', analysisQuery, analysisBody },
  );

  const sentence = useMemo(() => {
    if (!data?.rows?.[0] || !analysisTextTemplate) {
      return null;
    }

    const raw = Object.keys(data.rows[0]).reduce((res, key) => {
      if (!res) return null;
      let value: string | number = data.rows[0][key];
      if (typeof value === 'number') {
        value = value.toFixed(2);
      }
      if (!value) return null;
      return res.replace(`{{${key}}}`, `<strong>${value}</strong>`);
    }, analysisTextTemplate);
    if (!raw) return null;
    return typeof window !== 'undefined'
      ? DOMPurify.sanitize(raw, { ALLOWED_TAGS: ['strong', 'em', 'b', 'i', 'br', 'span'] })
      : raw.replace(/<[^>]*>/g, '');
  }, [data, analysisTextTemplate]);

  return (
    <div {...rootWidgetProps()}>
      <div className="name">{name}</div>
      {loaded && (
        <>
          {(noData || !sentence) && (
            <div className="widget-no-data">
              <h3>
                <T _str="NO DATA AVAILABLE" />
              </h3>
            </div>
          )}
          {!noData && (
            <>
              <div className="widget-text-chart" dangerouslySetInnerHTML={{ __html: sentence }} />
              {shortMeta && (
                <div className="meta-short">
                  {shortMeta}

                  {!noData && <DownloadCsv data={data} name={slug} />}

                  {info && (
                    <button
                      type="button"
                      className="btn-analysis btn-analysis__info"
                      data-info={info}
                      data-name={name}
                      title={t('View detailed info')}
                      onClick={() => InfoWindow.show(name, info)}
                    >
                      <svg className="icon icon-info">
                        <use xlinkHref="#icon-info" />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default TextChart;
